import { useState } from "react";
import { StarUser } from "./StarAuthModal";
import { dropshipProducts } from "@/mocks/dropship";

interface StarDashboardProps {
  user: StarUser;
  onLogout: () => void;
  onUpdateUser: (updated: StarUser) => void;
}

const platformIcons: Record<string, string> = {
  YouTube: "ri-youtube-line",
  TikTok: "ri-tiktok-line",
  Facebook: "ri-facebook-circle-line",
  "Twitter/X": "ri-twitter-x-line",
};

const platformColors: Record<string, string> = {
  YouTube: "text-red-500",
  TikTok: "text-stone-700",
  Facebook: "text-blue-600",
  "Twitter/X": "text-sky-500",
};

type Tab = "overview" | "products" | "edit";

const mockRecentSales = [
  { product: "Wireless Earbuds Pro X200", qty: 24, date: "Apr 9, 2026", commission: 107.96, platform: "YouTube" },
  { product: "Vitamin C Brightening Serum 30ml", qty: 38, date: "Apr 8, 2026", commission: 140.68, platform: "TikTok" },
  { product: "LED RGB Strip Light 10m", qty: 55, date: "Apr 7, 2026", commission: 148.40, platform: "Facebook" },
  { product: "Collagen Face Cream 50ml", qty: 19, date: "Apr 6, 2026", commission: 104.46, platform: "Twitter/X" },
  { product: "Portable Mini Projector 1080P", qty: 8, date: "Apr 5, 2026", commission: 71.99, platform: "YouTube" },
];

export default function StarDashboard({ user, onLogout, onUpdateUser }: StarDashboardProps) {
  const [tab, setTab] = useState<Tab>("overview");
  const [copiedId, setCopiedId] = useState<number | null>(null);
  const [shareModal, setShareModal] = useState<{ product: typeof dropshipProducts[0]; platform: string } | null>(null);

  // Edit profile state
  const [editName, setEditName] = useState(user.name);
  const [editBio, setEditBio] = useState(user.bio);
  const [editHandle, setEditHandle] = useState(user.platformHandle);
  const [editFollowers, setEditFollowers] = useState(user.followers);
  const [editCountry, setEditCountry] = useState(user.country);
  const [editPhoto, setEditPhoto] = useState(user.photo);
  const [editPhotoPreview, setEditPhotoPreview] = useState("");
  const [editPlatform, setEditPlatform] = useState(user.platform);
  const [editPhone, setEditPhone] = useState(user.phone || "");
  const [saveSuccess, setSaveSuccess] = useState(false);

  const handlePhotoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (ev) => {
        const result = ev.target?.result as string;
        setEditPhotoPreview(result);
        setEditPhoto(result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleSaveProfile = () => {
    onUpdateUser({
      ...user,
      name: editName,
      bio: editBio,
      platformHandle: editHandle,
      followers: editFollowers,
      country: editCountry,
      photo: editPhoto,
      platform: editPlatform,
      phone: editPhone,
    });
    setSaveSuccess(true);
    setTimeout(() => setSaveSuccess(false), 2500);
  };

  const handleCopyLink = (productId: number) => {
    const link = `https://crownglobal.com/dropship/product/${productId}?ref=${user.id}`;
    navigator.clipboard.writeText(link).catch(() => {});
    setCopiedId(productId);
    setTimeout(() => setCopiedId(null), 2000);
  };

  const platformShareLinks: Record<string, (product: typeof dropshipProducts[0]) => string> = {
    YouTube: (p) => `https://studio.youtube.com/channel/new?title=Check+out+${encodeURIComponent(p.name)}&description=${encodeURIComponent("Get it here: https://crownglobal.com/dropship/product/" + p.id + "?ref=" + user.id)}`,
    TikTok: (p) => `https://www.tiktok.com/upload?caption=${encodeURIComponent("🔥 " + p.name + " — link in bio! https://crownglobal.com/dropship/product/" + p.id + "?ref=" + user.id)}`,
    Facebook: (p) => `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent("https://crownglobal.com/dropship/product/" + p.id + "?ref=" + user.id)}&quote=${encodeURIComponent("Check out " + p.name + "!")}`,
    "Twitter/X": (p) => `https://twitter.com/intent/tweet?text=${encodeURIComponent("🛍️ " + p.name + " — amazing deal! " + "https://crownglobal.com/dropship/product/" + p.id + "?ref=" + user.id)}`,
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Dashboard header */}
      <div className="bg-white border-b border-gray-100 sticky top-0 z-30">
        <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full overflow-hidden bg-gray-100 shrink-0">
              <img src={editPhotoPreview || user.photo} alt={user.name} className="w-full h-full object-cover" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-800 text-sm">{user.name}</span>
                {user.verified && <i className="ri-shield-check-fill text-amber-400 text-sm"></i>}
              </div>
              <div className={`flex items-center gap-1 text-xs font-semibold ${platformColors[user.platform] || "text-gray-500"}`}>
                <i className={platformIcons[user.platform] || "ri-global-line"}></i>
                <span>{user.platformHandle || user.platform}</span>
              </div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden md:flex items-center gap-1 bg-amber-50 text-amber-600 text-xs font-semibold px-3 py-1.5 rounded-full">
              <i className="ri-money-dollar-circle-line"></i>
              Commission Rate: {user.commissionRate}%
            </div>
            <button onClick={onLogout} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-red-500 transition-colors cursor-pointer whitespace-nowrap">
              <i className="ri-logout-box-r-line"></i>
              <span className="hidden md:inline">Logout</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-screen-xl mx-auto px-4 md:px-8 py-8">
        {/* Tabs */}
        <div className="flex gap-1 bg-gray-100 rounded-xl p-1 w-fit mb-8">
          {([["overview", "ri-dashboard-line", "Overview"], ["products", "ri-store-2-line", "Products & Share"], ["edit", "ri-edit-line", "Edit Profile"]] as [Tab, string, string][]).map(([t, icon, label]) => (
            <button key={t} onClick={() => setTab(t)} className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors cursor-pointer whitespace-nowrap ${tab === t ? "bg-white text-amber-500" : "text-gray-500 hover:text-gray-700"}`}>
              <i className={icon}></i> {label}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {tab === "overview" && (
          <div>
            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: "Total Commission Earned", value: `$${user.totalEarned.toLocaleString()}`, icon: "ri-money-dollar-circle-line", color: "bg-amber-50 text-amber-500" },
                { label: "Products Sold", value: user.totalSold.toLocaleString(), icon: "ri-shopping-bag-3-line", color: "bg-green-50 text-green-500" },
                { label: "Commission Rate", value: `${user.commissionRate}%`, icon: "ri-percent-line", color: "bg-rose-50 text-rose-500" },
                { label: "Member Since", value: user.joinedDate, icon: "ri-calendar-line", color: "bg-sky-50 text-sky-500" },
              ].map(s => (
                <div key={s.label} className="bg-white rounded-xl p-5 flex items-center gap-4">
                  <div className={`w-11 h-11 flex items-center justify-center rounded-xl shrink-0 ${s.color}`}>
                    <i className={`${s.icon} text-xl`}></i>
                  </div>
                  <div>
                    <div className="text-lg font-extrabold text-gray-900">{s.value}</div>
                    <div className="text-xs text-gray-400">{s.label}</div>
                  </div>
                </div>
              ))}
            </div>

            {/* Recent sales */}
            <div className="bg-white rounded-xl overflow-hidden mb-8">
              <div className="px-6 py-4 border-b border-gray-100 flex items-center justify-between">
                <h3 className="font-bold text-gray-800">Recent Sales & Commissions</h3>
                <span className="text-xs text-gray-400">Last 30 days</span>
              </div>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-gray-50 text-xs text-gray-400 uppercase">
                      <th className="text-left px-6 py-3 font-semibold">Product</th>
                      <th className="text-left px-6 py-3 font-semibold">Platform</th>
                      <th className="text-left px-6 py-3 font-semibold">Qty</th>
                      <th className="text-left px-6 py-3 font-semibold">Date</th>
                      <th className="text-right px-6 py-3 font-semibold">Commission</th>
                    </tr>
                  </thead>
                  <tbody>
                    {mockRecentSales.map((sale, i) => (
                      <tr key={i} className="border-t border-gray-50 hover:bg-gray-50 transition-colors">
                        <td className="px-6 py-3 font-medium text-gray-800">{sale.product}</td>
                        <td className="px-6 py-3">
                          <span className={`flex items-center gap-1 text-xs font-semibold ${platformColors[sale.platform] || "text-gray-500"}`}>
                            <i className={platformIcons[sale.platform] || "ri-global-line"}></i>
                            {sale.platform}
                          </span>
                        </td>
                        <td className="px-6 py-3 text-gray-600">{sale.qty}</td>
                        <td className="px-6 py-3 text-gray-400">{sale.date}</td>
                        <td className="px-6 py-3 text-right font-bold text-green-600">+${sale.commission.toFixed(2)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* How to earn more */}
            <div className="bg-gradient-to-r from-amber-400 to-orange-400 rounded-2xl p-6 text-white">
              <h3 className="font-extrabold text-lg mb-2">Boost Your Earnings</h3>
              <p className="text-white/80 text-sm mb-4">Share more products on your {user.platform} channel to earn more commission. Each sale through your link earns you {user.commissionRate}%.</p>
              <button onClick={() => setTab("products")} className="bg-white text-amber-500 font-bold px-6 py-2.5 rounded-full text-sm hover:bg-amber-50 transition-colors cursor-pointer whitespace-nowrap">
                Browse Products to Share <i className="ri-arrow-right-line ml-1"></i>
              </button>
            </div>
          </div>
        )}

        {/* PRODUCTS & SHARE TAB */}
        {tab === "products" && (
          <div>
            <div className="mb-6">
              <h2 className="text-xl font-extrabold text-gray-900 mb-1">Products to Promote</h2>
              <p className="text-gray-500 text-sm">Pick products, copy your referral link, and share on your {user.platform} channel to earn {user.commissionRate}% commission per sale.</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {dropshipProducts.map(product => (
                <div key={product.id} className="bg-white rounded-xl overflow-hidden">
                  <div className="w-full h-44 overflow-hidden">
                    <img src={product.image} alt={product.name} className="w-full h-full object-cover object-top" />
                  </div>
                  <div className="p-4">
                    <div className="flex items-start justify-between mb-1">
                      <h3 className="font-bold text-gray-800 text-sm leading-tight flex-1 mr-2">{product.name}</h3>
                      <span className="text-xs bg-amber-50 text-amber-600 font-bold px-2 py-0.5 rounded-full shrink-0">{product.commission}%</span>
                    </div>
                    <p className="text-xs text-gray-400 mb-3 line-clamp-2">{product.description}</p>
                    <div className="flex items-center justify-between mb-3">
                      <span className="text-base font-extrabold text-gray-900">${product.price}</span>
                      <span className="text-xs text-green-600 font-semibold">
                        Earn: ${(product.price * product.commission / 100).toFixed(2)}/sale
                      </span>
                    </div>

                    {/* Share buttons */}
                    <div className="flex gap-2 mb-2">
                      <button
                        onClick={() => handleCopyLink(product.id)}
                        className={`flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 rounded-lg transition-colors cursor-pointer whitespace-nowrap ${copiedId === product.id ? "bg-green-100 text-green-600" : "bg-gray-100 text-gray-600 hover:bg-gray-200"}`}
                      >
                        <i className={copiedId === product.id ? "ri-check-line" : "ri-links-line"}></i>
                        {copiedId === product.id ? "Copied!" : "Copy Link"}
                      </button>
                      <button
                        onClick={() => setShareModal({ product, platform: user.platform })}
                        className="flex-1 flex items-center justify-center gap-1.5 text-xs font-semibold py-2 bg-amber-400 hover:bg-amber-500 text-white rounded-lg transition-colors cursor-pointer whitespace-nowrap"
                      >
                        <i className={platformIcons[user.platform] || "ri-share-line"}></i>
                        Share on {user.platform === "Twitter/X" ? "Twitter" : user.platform}
                      </button>
                    </div>

                    {/* All platform share */}
                    <div className="flex gap-1.5">
                      {Object.keys(platformIcons).map(p => (
                        <a
                          key={p}
                          href={platformShareLinks[p]?.(product) || "#"}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={`Share on ${p}`}
                          className={`flex-1 flex items-center justify-center py-1.5 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-pointer ${platformColors[p] || "text-gray-500"}`}
                        >
                          <i className={`${platformIcons[p]} text-sm`}></i>
                        </a>
                      ))}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* EDIT PROFILE TAB */}
        {tab === "edit" && (
          <div className="max-w-2xl">
            <h2 className="text-xl font-extrabold text-gray-900 mb-6">Edit Your Profile</h2>

            {saveSuccess && (
              <div className="bg-green-50 border border-green-200 text-green-700 text-sm rounded-xl px-4 py-3 mb-6 flex items-center gap-2">
                <i className="ri-checkbox-circle-line text-lg"></i> Profile updated successfully!
              </div>
            )}

            <div className="bg-white rounded-xl p-6 flex flex-col gap-5">
              {/* Photo */}
              <div className="flex items-center gap-5">
                <div className="w-20 h-20 rounded-full overflow-hidden bg-gray-100 shrink-0">
                  <img src={editPhotoPreview || editPhoto} alt="profile" className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Profile Photo</p>
                  <label className="flex items-center gap-2 text-sm bg-amber-50 text-amber-600 font-semibold px-4 py-2 rounded-lg cursor-pointer hover:bg-amber-100 transition-colors whitespace-nowrap">
                    <i className="ri-camera-line"></i> Change Photo
                    <input type="file" accept="image/*" className="hidden" onChange={handlePhotoChange} />
                  </label>
                  <p className="text-xs text-gray-400 mt-1">JPG, PNG up to 5MB</p>
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Full Name</label>
                  <input type="text" value={editName} onChange={e => setEditName(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Country</label>
                  <input type="text" value={editCountry} onChange={e => setEditCountry(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors" />
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Email Address</label>
                  <input type="email" value={user.email} readOnly className="w-full border border-gray-100 rounded-xl px-4 py-2.5 text-sm bg-gray-50 text-gray-400 cursor-not-allowed" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Phone Number</label>
                  <input type="tel" value={editPhone} onChange={e => setEditPhone(e.target.value)} placeholder="+1 555 123 4567" className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors" />
                </div>
              </div>

              {/* Platform */}
              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Primary Platform</label>
                <div className="grid grid-cols-4 gap-2">
                  {["YouTube", "TikTok", "Facebook", "Twitter/X"].map(p => (
                    <button key={p} onClick={() => setEditPlatform(p)} className={`flex flex-col items-center gap-1 py-2.5 rounded-xl border-2 text-xs font-semibold transition-colors cursor-pointer ${editPlatform === p ? "border-amber-400 bg-amber-50 text-amber-600" : "border-gray-200 text-gray-500 hover:border-amber-300"}`}>
                      <i className={`${platformIcons[p]} text-lg`}></i>
                      <span>{p === "Twitter/X" ? "Twitter" : p}</span>
                    </button>
                  ))}
                </div>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">@Handle / Channel Name</label>
                  <input type="text" value={editHandle} onChange={e => setEditHandle(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-gray-600 mb-1.5">Followers Count</label>
                  <input type="text" value={editFollowers} onChange={e => setEditFollowers(e.target.value)} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors" />
                </div>
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Bio</label>
                <textarea value={editBio} onChange={e => setEditBio(e.target.value)} rows={4} maxLength={300} className="w-full border border-gray-200 rounded-xl px-4 py-2.5 text-sm focus:outline-none focus:border-amber-400 transition-colors resize-none" />
                <p className="text-xs text-gray-400 mt-1">{editBio.length}/300 characters</p>
              </div>

              <div className="bg-amber-50 rounded-xl p-4 flex items-center gap-3">
                <i className="ri-information-line text-amber-500 text-lg shrink-0"></i>
                <p className="text-xs text-amber-700">Your commission rate is set by CrownGlobal based on your follower count and performance. Current rate: <strong>{user.commissionRate}%</strong></p>
              </div>

              <button onClick={handleSaveProfile} className="bg-amber-400 hover:bg-amber-500 text-white font-bold py-3 rounded-xl transition-colors cursor-pointer whitespace-nowrap">
                <i className="ri-save-line mr-2"></i> Save Changes
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Share modal */}
      {shareModal && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setShareModal(null)}>
          <div className="bg-white rounded-2xl w-full max-w-md p-6" onClick={e => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-bold text-gray-800">Share on {shareModal.platform}</h3>
              <button onClick={() => setShareModal(null)} className="w-8 h-8 flex items-center justify-center text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 mb-4">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-14 h-14 rounded-lg overflow-hidden shrink-0">
                  <img src={shareModal.product.image} alt={shareModal.product.name} className="w-full h-full object-cover" />
                </div>
                <div>
                  <p className="font-semibold text-gray-800 text-sm">{shareModal.product.name}</p>
                  <p className="text-xs text-gray-400">${shareModal.product.price} · {shareModal.product.commission}% commission</p>
                </div>
              </div>
              <p className="text-xs text-gray-500 bg-white rounded-lg p-3 border border-gray-200 font-mono break-all">
                https://crownglobal.com/dropship/product/{shareModal.product.id}?ref={user.id}
              </p>
            </div>
            <p className="text-xs text-gray-500 mb-4">Share this product on your {shareModal.platform} channel. Every sale through your link earns you <strong className="text-amber-500">${(shareModal.product.price * shareModal.product.commission / 100).toFixed(2)}</strong> commission.</p>
            <div className="flex gap-3">
              <button onClick={() => handleCopyLink(shareModal.product.id)} className="flex-1 flex items-center justify-center gap-2 border border-gray-200 text-gray-600 font-semibold py-2.5 rounded-xl hover:bg-gray-50 transition-colors cursor-pointer whitespace-nowrap text-sm">
                <i className="ri-links-line"></i> Copy Link
              </button>
              <a
                href={platformShareLinks[shareModal.platform]?.(shareModal.product) || "#"}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 flex items-center justify-center gap-2 bg-amber-400 hover:bg-amber-500 text-white font-bold py-2.5 rounded-xl transition-colors cursor-pointer whitespace-nowrap text-sm"
              >
                <i className={platformIcons[shareModal.platform] || "ri-share-line"}></i>
                Open {shareModal.platform === "Twitter/X" ? "Twitter" : shareModal.platform}
              </a>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
